import React from 'react';
import { Group, Text, Switch } from '@mantine/core';
import { EntityRole } from 'app/models/entity-profile-params';

interface RolesSectionProps {
    roles: EntityRole[];
    errorMsg?: string | null;
    onToggleRole: (role: EntityRole, checked: boolean) => void;
}

export interface Option {
    label: string;
    value: string;
}

export const roleLabels: { [key in EntityRole]: string } = {
    [EntityRole.BorrowerRole]: 'Borrower',
    [EntityRole.LenderRole]: 'Lender',
    [EntityRole.LetterOfCreditRole]: 'Letter Of Credit (Beneficiary)',
    [EntityRole.GuarantorRole]: 'Guarantor',
    [EntityRole.ThirdPartyRole]: 'Third Party',
    [EntityRole.AdminAgentRole]: 'Admin Agent',
    [EntityRole.CustodianRole]: 'Custodian',
    [EntityRole.TrusteeRole]: 'Trustee',
    [EntityRole.ServicerRole]: 'Servicer',
    [EntityRole.BusinessProcessOutsourcerRole]: 'Business Process Outsourcer',
    [EntityRole.FundAdminRole]: 'Fund Admin',
    [EntityRole.FundManagerParentRole]: 'Fund Manager / Parent',
    [EntityRole.PrincipalRole]: 'Principal',
    [EntityRole.SecurityAgentRole]: 'Security Agent',
    [EntityRole.LegalRole]: 'Legal',
    [EntityRole.FundSubaccountRole]: 'Fund / Subaccount',
};

export const thirdPartyProviderRoleOptions = [
    { label: 'Trustee', value: EntityRole.TrusteeRole },
    { label: 'Custodian', value: EntityRole.CustodianRole },
    { label: 'Fund Admin', value: EntityRole.FundAdminRole },
    { label: 'Business Process Outsourcer', value: EntityRole.BusinessProcessOutsourcerRole },
    { label: 'Legal', value: EntityRole.LegalRole },
];

const RolesSection: React.FC<RolesSectionProps> = ({ roles, errorMsg, onToggleRole }) => {
    const allRoles: EntityRole[] = [
        EntityRole.AdminAgentRole,
        EntityRole.BorrowerRole,
        EntityRole.BusinessProcessOutsourcerRole,
        EntityRole.CustodianRole,
        EntityRole.FundAdminRole,
        EntityRole.GuarantorRole,
        EntityRole.LenderRole,
        EntityRole.LetterOfCreditRole,
        EntityRole.ServicerRole,
        EntityRole.ThirdPartyRole,
        EntityRole.TrusteeRole,
        EntityRole.FundManagerParentRole,
        EntityRole.PrincipalRole,
        EntityRole.SecurityAgentRole,
        EntityRole.LegalRole,
        EntityRole.FundSubaccountRole,
    ];

    // sort all rows alphabetically
    const sortedRoles = [...allRoles].sort((a, b) =>
        roleLabels[a].localeCompare(roleLabels[b])
    );

    // Convert sorted list so names are displayed alphabetically by column first and then by row
    const COLUMNS = 4; // Number of columns displayed
    const sortedByColumnFirstRoles = Array.from({ length: COLUMNS }, (_, col) =>
        sortedRoles.filter((_, index) => index % COLUMNS === col)
    ).flat();

    return (
        <div className="roles-container" >
            <Text className="roles-title">
                Roles:
            </Text>
            <Group
                spacing="md"
                mb="md"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gap: '10px',
                }}
            >
                {sortedByColumnFirstRoles.map((role) => (
                    <div key={role} className="role-item">
                        <Switch
                            checked={roles.includes(role ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleRole(role, e.currentTarget.checked)}
                            size="md"
                            classNames={{
                                track: `custom-switch-track ${roles.includes(role) ? 'active' : ''}`,
                                thumb: `custom-switch-thumb ${roles.includes(role) ? 'active' : 'inactive'}`,
                            }}
                        />
                        <Text className="role-text">
                            {roleLabels[role]}
                        </Text>
                    </div>
                ))}
            </Group >
            {errorMsg && <Text color="red">{errorMsg}</Text>}
        </div>
    );
};

export default RolesSection;
